/**
 * Keeps dashboard cards from landing on top of one another.
 *
 * Cards are placed by hand, so the only rule is that two of them may not
 * occupy the same space. When a drop would overlap something, the card being
 * moved is nudged to the nearest position that doesn't — and nothing else
 * moves. That last part matters: the other cards are where the teacher put
 * them, and a board that rearranges itself because one card was nudged would
 * be worse than the overlap.
 */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function overlapping(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

/**
 * The nearest free spot to where the card was dropped.
 *
 * Candidates are the drop point itself plus every edge of every other card —
 * flush left of one, flush right of another, just below a third. Anywhere
 * else is either the same or leaves an unusable sliver, so those are the only
 * positions worth considering. The closest one to where the pointer let go
 * wins, which is what makes it feel like the card settled rather than jumped.
 */
export function resolveCollision(
  wanted: Rect,
  others: Rect[],
  canvasWidth: number,
  gap: number,
): { x: number; y: number } {
  const fits = (x: number, y: number) =>
    x >= 0 &&
    y >= 0 &&
    x + wanted.width <= canvasWidth + 0.5 &&
    !others.some((o) => overlapping({ ...wanted, x, y }, o));

  if (fits(wanted.x, wanted.y)) return { x: wanted.x, y: wanted.y };

  const xs = new Set<number>([0, wanted.x]);
  const ys = new Set<number>([0, wanted.y]);

  for (const o of others) {
    xs.add(o.x + o.width + gap);
    xs.add(o.x - wanted.width - gap);
    ys.add(o.y + o.height + gap);
    ys.add(o.y - wanted.height - gap);
  }

  let best: { x: number; y: number; distance: number } | null = null;

  for (const x of xs) {
    for (const y of ys) {
      if (!fits(x, y)) continue;

      const distance = (x - wanted.x) ** 2 + (y - wanted.y) ** 2;
      if (!best || distance < best.distance) best = { x, y, distance };
    }
  }

  // Nothing fits anywhere on the current rows: drop it below everything. There
  // is always room down there, because the board scrolls.
  if (!best) {
    const floor = others.reduce((lowest, o) => Math.max(lowest, o.y + o.height), 0);
    return {
      x: Math.min(Math.max(wanted.x, 0), Math.max(0, canvasWidth - wanted.width)),
      y: floor + gap,
    };
  }

  return { x: best.x, y: best.y };
}

/**
 * The widest a card may be from a given left edge, so a resize can't grow it
 * through its neighbour.
 */
export function maxWidthAt(card: Rect, others: Rect[], canvasWidth: number, gap: number): number {
  let limit = canvasWidth - card.x;

  for (const o of others) {
    // Only cards level with this one can block it.
    const sameRows = card.y < o.y + o.height && o.y < card.y + card.height;
    if (sameRows && o.x >= card.x) limit = Math.min(limit, o.x - gap - card.x);
  }

  return Math.max(limit, 0);
}

/**
 * The tallest a card may be before it runs into something below it.
 *
 * Unbounded when nothing is in the way: the board scrolls, so a card can be
 * as long as it likes if there is nothing underneath.
 */
export function maxHeightAt(card: Rect, others: Rect[], gap: number): number {
  let limit = Infinity;

  for (const o of others) {
    const sameColumns = card.x < o.x + o.width && o.x < card.x + card.width;
    if (sameColumns && o.y >= card.y) limit = Math.min(limit, o.y - gap - card.y);
  }

  return Math.max(limit, 0);
}
